import React, { useCallback, useEffect, useState } from "react";

import T, { GetLang } from "./translations/Translations";
import { getEducationalTrackWords, getKinderWords, getLanguageWords, getStatusWords } from "@const/SchoolChoice";

import type { SchoolChoice } from "@/types/GlobalState";

interface SchoolCardProps {
    school: SchoolChoice;
}
/**
 * @name SchoolCard
 * @description SchoolCard component
 * @param {{}} school
 * @returns {JSX.Element}
 */
const SchoolCard = ({ school }: SchoolCardProps): React.JSX.Element => {
    const lang = GetLang();
    const { educationalTrack, kinderChoice, kinderOffer, languageChoice, languageOffer, locationDto, status } = school;
    const imgName = languageOffer ? `${locationDto.key}-${languageOffer}` : locationDto.key;
    const schoolName =
        educationalTrack && educationalTrack !== "NOT_APPLICABLE"
            ? `${locationDto.name} ${getEducationalTrackWords(educationalTrack)}`
            : locationDto.name;

    const defaultImgSrc = `${globalThis.location.origin}/images/default.png`;
    const initialImgSrc = imgName ? `${globalThis.location.origin}/images/${imgName}.png` : defaultImgSrc;

    const [imgSrc, setImgSrc] = useState<string>(initialImgSrc);

    useEffect(() => {
        setImgSrc(initialImgSrc);
    }, [initialImgSrc]);

    const handleImageError = useCallback((): void => {
        setImgSrc((current) => (current !== defaultImgSrc ? defaultImgSrc : current));
    }, [defaultImgSrc]);

    return (
        <div className="school-card">
            <div className="school-card-container">
                <div className="school-card-header">{schoolName}</div>
                <div className="school-card-body">
                    <div className="school-card-img-container">
                        {locationDto && (
                            <img
                                alt={locationDto.name}
                                height={80}
                                onError={handleImageError}
                                src={imgSrc}
                                title={locationDto.name}
                                width={80}
                            />
                        )}
                    </div>
                    <div className="school-card-data-container">
                        <div className={`school-card-data ${status}`}>
                            <div className="school-card-data-header">
                                {/* Request Status: */}
                                {T({ tKey: "OEApp_RequestStatus", langStr: lang })}:
                            </div>
                            <hr />
                            <div className="school-card-status">
                                <div className={`status-block ${status}`} />
                                {getStatusWords(status, lang)}
                            </div>
                        </div>
                        {kinderChoice && (
                            <div className="school-card-data">
                                <div className="school-card-data-header">
                                    {/* Kinder Choice: */}
                                    {T({ tKey: "OEApp_KinderChoice", langStr: lang })}:
                                </div>
                                <hr />
                                <div>{getKinderWords(kinderChoice, lang)}</div>
                            </div>
                        )}
                        {kinderOffer && (
                            <div className="school-card-data">
                                <div className="school-card-data-header">
                                    {/* Kinder Offer: */}
                                    {T({ tKey: "OEApp_KinderOffer", langStr: lang })}:
                                </div>
                                <hr />
                                <div className="school-card-choice">{getKinderWords(kinderOffer, lang)}</div>
                            </div>
                        )}
                        {languageChoice && (
                            <div className="school-card-data">
                                <div className="school-card-data-header">
                                    {/* Language Choice: */}
                                    {T({ tKey: "OEApp_LanguageChoice", langStr: lang })}:
                                </div>
                                <hr />
                                <div>{getLanguageWords(languageChoice, lang)}</div>
                            </div>
                        )}
                        {languageOffer && (
                            <div className="school-card-data">
                                <div className="school-card-data-header">
                                    {/* Language Offer: */}
                                    {T({ tKey: "OEApp_LanguageOffer", langStr: lang })}:
                                </div>
                                <hr />
                                <div>{getLanguageWords(languageOffer, lang)}</div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default SchoolCard;
